import { parentTreeItem } from "library/tree/index.mjs"
import { confirm } from "lib/@dialogs/confirm"
import { getLegislationUsingDestination } from "./controller/get-legislation-using-destination"
import { updateAuthoringLegislationGroup } from "./controller/update-authoring-legislation-group"

export const LEGISLATION_FOLDER = "folder"
export const LEGISLATION = "legislation"

export function buildLegislationUrlChangeWarning(scheduleCount) {
    const n = scheduleCount ?? 0
    return `This legislation is referenced by ${n} published ${n === 1 ? "schedule" : "schedules"}. Changing the URL will update future renders and exports, but documents that have already been distributed will still contain the previous URL.`
}

export async function confirmLegislationUrlChange({ scheduleCount, previousUrl, nextUrl }) {
    if (!scheduleCount) return true
    if ((nextUrl ?? "") === (previousUrl ?? "")) return true
    return confirm(buildLegislationUrlChangeWarning(scheduleCount), "Change URL?")
}

// Soft warning when another legislation already points at the same destination — two
// records can legitimately cite the same page
export async function confirmDuplicateDestination(url, excludeId = null) {
    const duplicate = await getLegislationUsingDestination({ url, excludeId })
    if (!duplicate) return true
    return confirm(`This destination is already used by "${duplicate.title}". Save anyway?`, "Duplicate destination")
}

export function buildLegislationGraph(state, payload) {
    const { folders = [], legislation = [], rootIds = [], groupsByLegId = {} } = payload ?? {}

    for (const f of folders) {
        state.lookup.set(f.id, {
            type: LEGISLATION_FOLDER,
            id: f.id,
            legacyId: f.legacyId ?? null,
            name: f.name,
            parentId: f.parentId ?? null,
            lastModified: f.lastModified ?? null,
            scheduleCount: 0,
            children: [],
            [parentTreeItem]: null,
        })
    }
    for (const l of legislation) {
        state.lookup.set(l.id, {
            type: LEGISLATION,
            id: l.id,
            legacyId: l.legacyId ?? null,
            redirectId: l.redirectId ?? null,
            name: l.name ?? l.title,
            title: l.title,
            url: l.url,
            description: l.description ?? null,
            lastModified: l.lastModified ?? null,
            scheduleCount: l.scheduleCount ?? 0,
            scheduleIds: l.scheduleIds ?? [],
            schedules: l.schedules ?? [],
            [parentTreeItem]: new Set(),
        })
    }
    for (const f of folders) {
        const folder = state.lookup.get(f.id)
        if (!folder) continue
        for (const childId of f.childIds ?? []) {
            const child = state.lookup.get(childId)
            if (!child) continue
            folder.children.push(child)
            if (child.type === LEGISLATION_FOLDER) child[parentTreeItem] = folder
            else child[parentTreeItem].add(folder)
        }
    }
    for (const [legId, parentIds] of Object.entries(groupsByLegId)) {
        const node = state.lookup.get(legId)
        if (node?.type !== LEGISLATION) continue
        for (const pid of parentIds) {
            const parent = state.lookup.get(pid)
            if (parent?.type === LEGISLATION_FOLDER) node[parentTreeItem].add(parent)
        }
    }
    state.rootIds = [...rootIds]
}

export function getChildren(state, folderId) {
    const folder = state.lookup.get(folderId)
    if (folder?.type !== LEGISLATION_FOLDER) return []
    return folder.children
}

export function getPath(state, id) {
    const path = []
    let current = state.lookup.get(id)
    while (current) {
        path.unshift(current)
        const parent = current.type === LEGISLATION_FOLDER ? current[parentTreeItem] : null
        current = parent
    }
    return path
}

export function getGroupsForLegislation(state, legId) {
    const node = state.lookup.get(legId)
    if (node?.type !== LEGISLATION) return []
    return [...node[parentTreeItem]].map((f) => f.id)
}

export function getDescendantFolderIds(state, folderId) {
    const folder = state.lookup.get(folderId)
    if (folder?.type !== LEGISLATION_FOLDER) return []
    const ids = []
    for (const child of folder.children) {
        if (child.type !== LEGISLATION_FOLDER) continue
        ids.push(child.id, ...getDescendantFolderIds(state, child.id))
    }
    return ids
}

// Unwraps a destination field's value to a folder id — distinguishes "chose root" (null) from "chose nothing" (undefined), since both are otherwise falsy.
export function resolveDestinationId(value) {
    if (!value) return undefined
    return typeof value === "object" ? value.id : value
}

export async function saveFolderEdit({ id, name, destinationParentId }) {
    const group = destinationParentId !== undefined ? { name, parentId: destinationParentId } : { name }
    const folder = await updateAuthoringLegislationGroup({ id, group })
    return { folder }
}

// Mirrors a saved folderIds list (undefined if the Manage Location dialog was untouched) into the
// local tree; the server applies the same diff inside updateAuthoringLegislation. Shared by both
// places legislation can be edited (the list's Edit dialog and the detail page's inline form).
export function applyLegislationFolderDiff(state, { legislationId, folderIds }) {
    if (folderIds === undefined) return
    const currentParentIds = getGroupsForLegislation(state, legislationId)
    const addParentIds = folderIds.filter((id) => !currentParentIds.includes(id))
    const removeParentIds = currentParentIds.filter((id) => !folderIds.includes(id))
    if (!addParentIds.length && !removeParentIds.length) return
    for (const parentId of addParentIds) placeLegislation(state, { legislationId, parentId }, { silent: true })
    for (const parentId of removeParentIds) unplaceLegislation(state, legislationId, parentId, { silent: true })
    state.refresh()
}

export function setFolder(state, folder, { silent = false } = {}) {
    const existing = state.lookup.get(folder.id)
    if (existing?.type === LEGISLATION_FOLDER) {
        const oldParentId = existing.parentId
        Object.assign(existing, {
            legacyId: folder.legacyId ?? existing.legacyId,
            name: folder.name ?? existing.name,
            parentId: folder.parentId ?? null,
            lastModified: folder.lastModified ?? existing.lastModified,
        })
        if (existing.parentId !== oldParentId) {
            detachFolderFromParent(state, existing, oldParentId)
            attachFolderToParent(state, existing)
        }
    } else {
        const node = {
            type: LEGISLATION_FOLDER,
            id: folder.id,
            legacyId: folder.legacyId ?? null,
            name: folder.name,
            parentId: folder.parentId ?? null,
            lastModified: folder.lastModified ?? null,
            scheduleCount: 0,
            children: [],
            [parentTreeItem]: null,
        }
        state.lookup.set(folder.id, node)
        attachFolderToParent(state, node)
    }
    if (!silent) state.refresh()
}

export function removeFolder(state, removedGroupIds) {
    for (const id of removedGroupIds) {
        const node = state.lookup.get(id)
        if (node?.type !== LEGISLATION_FOLDER) continue
        cleanupSubtree(state, node)
        detachFolderFromParent(state, node, node.parentId)
        state.lookup.delete(id)
    }
    state.refresh()
}

export function setLegislation(state, legislation, { silent = false } = {}) {
    const existing = state.lookup.get(legislation.id)
    if (existing?.type === LEGISLATION) {
        Object.assign(existing, {
            legacyId: legislation.legacyId ?? existing.legacyId,
            redirectId: legislation.redirectId ?? existing.redirectId,
            name: legislation.name ?? legislation.title ?? existing.name,
            title: legislation.title ?? existing.title,
            url: legislation.url ?? existing.url,
            description: legislation.description ?? existing.description,
            lastModified: legislation.lastModified ?? existing.lastModified,
            scheduleCount: legislation.scheduleCount ?? existing.scheduleCount,
            scheduleIds: legislation.scheduleIds ?? existing.scheduleIds,
            schedules: legislation.schedules ?? existing.schedules,
        })
    } else {
        const {
            id,
            legacyId,
            redirectId,
            title,
            url,
            description,
            lastModified,
            scheduleCount,
            scheduleIds,
            schedules,
        } = legislation
        state.lookup.set(id, {
            type: LEGISLATION,
            id,
            legacyId: legacyId ?? null,
            redirectId: redirectId ?? null,
            title,
            url,
            description: description ?? null,
            lastModified: lastModified ?? null,
            scheduleCount: scheduleCount ?? 0,
            scheduleIds: scheduleIds ?? [],
            schedules: schedules ?? [],
            [parentTreeItem]: new Set(),
        })
    }
    if (!silent) state.refresh()
}

export function removeLegislation(state, id) {
    const node = state.lookup.get(id)
    if (node?.type !== LEGISLATION) return
    for (const folder of node[parentTreeItem]) folder.children.remove(node)
    state.lookup.delete(id)
    state.refresh()
}

export function placeLegislation(state, result, { silent = false } = {}) {
    const isCreate = result.legislation !== undefined
    const groupId = isCreate ? result.junction.parentId : result.parentId
    const folder = state.lookup.get(groupId)
    if (folder?.type !== LEGISLATION_FOLDER) return

    let legislation = isCreate ? state.lookup.get(result.legislation.id) : state.lookup.get(result.legislationId)
    if (isCreate && !legislation) {
        const {
            id,
            legacyId,
            redirectId,
            title,
            url,
            description,
            lastModified,
            scheduleCount,
            scheduleIds,
            schedules,
        } = result.legislation
        legislation = {
            type: LEGISLATION,
            id,
            legacyId: legacyId ?? null,
            redirectId: redirectId ?? null,
            title,
            url,
            description: description ?? null,
            lastModified: lastModified ?? null,
            scheduleCount: scheduleCount ?? 0,
            scheduleIds: scheduleIds ?? [],
            schedules: schedules ?? [],
            [parentTreeItem]: new Set(),
        }
        state.lookup.set(id, legislation)
    }
    if (!legislation) return

    if (!folder.children.includes(legislation)) folder.children.push(legislation)
    legislation[parentTreeItem].add(folder)
    if (!silent) state.refresh()
}

export function unplaceLegislation(state, legislationId, groupId, { silent = false } = {}) {
    const folder = state.lookup.get(groupId)
    const legislation = state.lookup.get(legislationId)
    if (folder?.type === LEGISLATION_FOLDER) folder.children.remove(legislation)
    if (legislation?.type === LEGISLATION && folder) legislation[parentTreeItem].delete(folder)
    if (!silent) state.refresh()
}

export function isFolderNonEmpty(state, folderId) {
    const folder = state.lookup.get(folderId)
    return folder?.type === LEGISLATION_FOLDER && folder.children.length > 0
}

export function folderHasOrphanedLegislation(state, folderId) {
    return anyOrphanedLegislation(state, folderId, () => true)
}

export function folderDeleteWouldRemoveProtectedLegislation(state, folderId) {
    return anyOrphanedLegislation(state, folderId, (legislation) => (legislation.scheduleCount ?? 0) > 0)
}

function anyOrphanedLegislation(state, folderId, predicate) {
    const folder = state.lookup.get(folderId)
    if (folder?.type !== LEGISLATION_FOLDER) return false
    const subtreeFolderIds = new Set()
    collectSubtreeFolderIds(state, folderId, subtreeFolderIds)
    return walkForOrphan(state, folderId, subtreeFolderIds, predicate, new Set())
}

function collectSubtreeFolderIds(state, folderId, into) {
    into.add(folderId)
    const folder = state.lookup.get(folderId)
    if (folder?.type !== LEGISLATION_FOLDER) return
    for (const child of folder.children) {
        if (child.type === LEGISLATION_FOLDER) collectSubtreeFolderIds(state, child.id, into)
    }
}

function walkForOrphan(state, folderId, subtreeFolderIds, predicate, seen) {
    const folder = state.lookup.get(folderId)
    if (folder?.type !== LEGISLATION_FOLDER) return false
    for (const child of folder.children) {
        if (child.type === LEGISLATION) {
            if (seen.has(child.id)) continue
            seen.add(child.id)
            const parents = [...child[parentTreeItem]]
            const isOrphan = parents.length && parents.every((parent) => subtreeFolderIds.has(parent.id))
            if (isOrphan && predicate(child)) return true
        } else if (
            child.type === LEGISLATION_FOLDER &&
            walkForOrphan(state, child.id, subtreeFolderIds, predicate, seen)
        ) {
            return true
        }
    }
    return false
}

function attachFolderToParent(state, folder) {
    const { parentId } = folder ?? {}
    if (parentId) {
        const parent = state.lookup.get(parentId)
        if (parent?.type === LEGISLATION_FOLDER) {
            if (!parent.children.includes(folder)) parent.children.push(folder)
            folder[parentTreeItem] = parent
        }
        return
    }
    folder[parentTreeItem] = null
    if (!state.rootIds.includes(folder.id)) state.rootIds.push(folder.id)
}

function detachFolderFromParent(state, folder, parentId) {
    if (parentId) {
        const parent = state.lookup.get(parentId)
        if (parent?.type === LEGISLATION_FOLDER) parent.children.remove(folder)
        return
    }
    state.rootIds.remove(folder.id)
}

function cleanupSubtree(state, folder) {
    for (const child of [...folder.children]) {
        if (child.type === LEGISLATION) {
            child[parentTreeItem].delete(folder)
            if (!child[parentTreeItem].size) state.lookup.delete(child.id)
        } else if (child.type === LEGISLATION_FOLDER) {
            cleanupSubtree(state, child)
            state.lookup.delete(child.id)
        }
    }
    folder.children = []
}
